import { useState } from 'react';
import { X, Loader2, LogIn, Search, Building2 } from 'lucide-react';
import type { RegisterVisitDTO, Supplier } from '@yawi/types';
import { useI18n } from '@/shared/i18n';
import { useFormValidation } from '@/shared/hooks/useFormValidation';

interface VisitFormProps {
  suppliers: Supplier[];
  selectedSupplier?: Supplier | null;
  onSubmit: (supplierId: string, data: RegisterVisitDTO) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  submissionError?: unknown;
}

export const VisitForm = ({
  suppliers,
  selectedSupplier: initialSupplier,
  onSubmit,
  onCancel,
  loading,
  submissionError
}: VisitFormProps) => {
  const { t } = useI18n();
  const { getFieldError, clearFieldError } = useFormValidation({ submissionError });

  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(initialSupplier || null);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);

  const [formData, setFormData] = useState<RegisterVisitDTO>({
    purpose: '',
    authorizedByName: '',
    vehiclePlate: '',
    notes: '',
  });

  const filteredSuppliers = suppliers.filter(s =>
    s.name.toLowerCase().includes(supplierSearch.toLowerCase()) ||
    s.category.toLowerCase().includes(supplierSearch.toLowerCase())
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSupplier || !formData.purpose.trim()) return;
    await onSubmit(selectedSupplier.id, formData);
  };

  const selectSupplier = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    setSupplierSearch('');
    setShowSupplierDropdown(false);
    clearFieldError('supplierId');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
              <LogIn className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-800">{t('suppliers.checkIn')}</h2>
              <p className="text-sm text-slate-500">{t('suppliers.registerEntry')}</p>
            </div>
          </div>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Supplier Selector */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {t('suppliers.title')} *
            </label>

            {selectedSupplier ? (
              <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                    <Building2 className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">{selectedSupplier.name}</p>
                    <p className="text-xs text-slate-500">
                      {t(`suppliers.category.${selectedSupplier.category.toLowerCase()}`)}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedSupplier(null)}
                  className="text-sm text-green-600 hover:text-green-700 font-medium"
                >
                  {t('common.edit')}
                </button>
              </div>
            ) : (
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    value={supplierSearch}
                    onChange={(e) => {
                      setSupplierSearch(e.target.value);
                      setShowSupplierDropdown(true);
                    }}
                    onFocus={() => setShowSupplierDropdown(true)}
                    placeholder={t('suppliers.searchPlaceholder')}
                    className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                      getFieldError('supplierId') ? 'border-red-300 bg-red-50' : 'border-slate-300'
                    }`}
                  />
                </div>

                {showSupplierDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredSuppliers.length > 0 ? (
                      filteredSuppliers.map((supplier) => (
                        <button
                          key={supplier.id}
                          type="button"
                          onClick={() => selectSupplier(supplier)}
                          className="w-full px-4 py-3 text-left hover:bg-slate-50 flex items-center gap-3 border-b border-slate-100 last:border-b-0"
                        >
                          <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center">
                            <Building2 className="w-4 h-4 text-slate-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">{supplier.name}</p>
                            <p className="text-xs text-slate-500">
                              {t(`suppliers.category.${supplier.category.toLowerCase()}`)}
                            </p>
                          </div>
                        </button>
                      ))
                    ) : (
                      <div className="px-4 py-3 text-sm text-slate-500 text-center">
                        {t('suppliers.noSuppliers')}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {getFieldError('supplierId') && (
              <p className="mt-1 text-xs text-red-500">{getFieldError('supplierId')}</p>
            )}
          </div>

          {/* Purpose */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {t('suppliers.visitPurpose')} *
            </label>
            <textarea
              value={formData.purpose}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, purpose: e.target.value }));
                clearFieldError('purpose');
              }}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none ${
                getFieldError('purpose') ? 'border-red-300 bg-red-50' : 'border-slate-300'
              }`}
              rows={2}
              required
              placeholder={t('suppliers.visitPurposePlaceholder')}
            />
            {getFieldError('purpose') && (
              <p className="mt-1 text-xs text-red-500">{getFieldError('purpose')}</p>
            )}
          </div>

          {/* Authorized By & Vehicle Plate */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {t('suppliers.authorizedBy')}
              </label>
              <input
                type="text"
                value={formData.authorizedByName || ''}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, authorizedByName: e.target.value }));
                  clearFieldError('authorizedByName');
                }}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  getFieldError('authorizedByName') ? 'border-red-300 bg-red-50' : 'border-slate-300'
                }`}
                placeholder={t('suppliers.authorizedByPlaceholder')}
              />
              {getFieldError('authorizedByName') && (
                <p className="mt-1 text-xs text-red-500">{getFieldError('authorizedByName')}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {t('suppliers.vehiclePlate')}
              </label>
              <input
                type="text"
                value={formData.vehiclePlate || ''}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, vehiclePlate: e.target.value.toUpperCase() }));
                  clearFieldError('vehiclePlate');
                }}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono ${
                  getFieldError('vehiclePlate') ? 'border-red-300 bg-red-50' : 'border-slate-300'
                }`}
                placeholder="ABC-123"
              />
              {getFieldError('vehiclePlate') && (
                <p className="mt-1 text-xs text-red-500">{getFieldError('vehiclePlate')}</p>
              )}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {t('common.notes')}
            </label>
            <textarea
              value={formData.notes || ''}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, notes: e.target.value }));
                clearFieldError('notes');
              }}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none ${
                getFieldError('notes') ? 'border-red-300 bg-red-50' : 'border-slate-300'
              }`}
              rows={2}
            />
            {getFieldError('notes') && (
              <p className="mt-1 text-xs text-red-500">{getFieldError('notes')}</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
              disabled={loading}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading || !selectedSupplier || !formData.purpose.trim()}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <LogIn className="w-4 h-4" />
              )}
              {t('suppliers.registerEntry')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
